import { dataURLtoFile } from 'utils';
import apiSlice from '..';
import {
  CheckboxField,
  ImageField,
  InteriorQuestions,
  SectionConfig,
  Section,
  SectionNotes,
  SectionImage
} from './types';
import { generateRandomId } from 'utils/crypto';
import { VehicleSection } from 'types/vehicle';
import { db } from 'db';
import { fetchBaseQuery, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query';
import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';

export type SectionsOrder = Array<[keyof SectionConfig | 'dealer_comments', string[]]>;

export const defaultSectionOrder: SectionsOrder = [
  ['odb', ['odb_check_readiness']],
  [
    'underhood_mechanical',
    [
      'oil_or_dipstick',
      'noticeable_oil_leak',
      'transmission_dipstick',
      'coolant',
      'head_gasket_condition',
      'transmission_dipstick_smell',
      'underhood_equipment',
      'check_blow_by',
      'unhood_frame_check'
    ]
  ],
  ['tires_and_wheels', ['driver_front', 'driver_rear', 'passenger_rear', 'passenger_front']],
  [
    'exterior',
    [
      'exterior_pictures',
      'vin_plate_pictures',
      'license_plate',
      'keys',
      'under_body_and_frame',
      'trunk_and_rear_frame',
      'catalytic_converter_present',
      'suspension_alternations',
      'other_glass_damage',
      'windshield',
      'b_and_c_frame_check'
    ]
  ],
  ['title_issues', []],
  ['interior', ['interior_pictures', 'interior_functional']],
  ['power_equipment', []],
  [
    'test_drive',
    [
      'how_engine_runs',
      'engine_rev_up',
      'engine_light_acceleration',
      'engine_medium_acceleration',
      'engine_heavy_acceleration',
      'any_engine_noise',
      'exhaust_sounds_like',
      'how_transmission_shifts',
      'transmission_light_acceleration',
      'transmission_medium_acceleration',
      'transmission_heavy_acceleration',
      'transmission_shudder',
      'clutch_pedal',
      'clutch_engages_in_gear',
      'engaged_every_gear',
      'three_point_turn',
      'engage_in_low_4x4',
      'engage_in_high_4x4'
    ]
  ],
  ['dealer_comments', []]
];

type AddCheckboxArgs = {
  vehicleId: number;
  sectionId: string;
  payload: { selected: boolean; label: string };
};

type UpdateCheckboxArgs = {
  checkboxId: number;
  vehicleId: number;
  sectionId: number;
  payload: { selected?: boolean; notes?: string; label?: string };
};

type RemoveCheckboxArgs = {
  checkboxId: number;
  vehicleId: number;
  sectionId: number;
};

export type PostSyncUpdates = Record<number, { notesId: number; url?: string; url_thumb?: string; video?: string }>;

export type SyncListerFormResponse = {
  notesEntityIdMap: {
    section: PostSyncUpdates;
    image: PostSyncUpdates;
    checkbox: PostSyncUpdates;
  };
  checkboxIdMap: Record<string, { id: number; notesId: number }>;
  imagesIdMap: Record<string, { id: number; notesId: number; url: string; url_thumb: string }>;
};

export const vehicleSectionsApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getSectionConfig: builder.query<SectionConfig, void>({
      query: () => ({
        url: 'vehicles/sections/config',
        method: 'GET'
      }),
      transformResponse: (response: any) => response.data,
      keepUnusedDataFor: 3600
    }),
    getSectionsOrder: builder.query<SectionsOrder, void>({
      query: () => ({
        url: 'vehicles/upload/section-order',
        method: 'GET'
      }),
      transformResponse: (r: any) => r.data || defaultSectionOrder,
      keepUnusedDataFor: 3600
    }),
    updateSectionsOrder: builder.mutation<void, SectionsOrder>({
      query: sectionOrder => ({
        url: 'vehicles/upload/section-order',
        method: 'PUT',
        body: sectionOrder
      })
    }),
    getObdImages: builder.query<SectionImage[], number>({
      query: vehicleId => ({
        url: `vehicles/${vehicleId}/sections/obd`,
        method: 'GET'
      }),
      transformResponse: (r: any) => r.data,
      keepUnusedDataFor: 0
    }),
    saveObdImages: builder.mutation<void, { vehicleId: number; codes?: Blob; smog?: Blob; odometer?: Blob }>({
      query: ({ vehicleId, codes, smog, odometer }) => {
        const formData = new FormData();
        formData.append('codes.file', codes || '');
        formData.append('smog.file', smog || '');
        formData.append('odometer.file', odometer || '');
        return {
          url: `vehicles/${vehicleId}/sections/obd`,
          method: 'POST',
          body: formData,
          formData: true
        };
      },
      transformResponse: (r: any) => r.data
    }),
    getQuestions: builder.query<{ interior_questions: InteriorQuestions }, number>({
      query: vehicleId => ({
        url: `vehicles/${vehicleId}/sections/questions`,
        method: 'GET'
      }),
      transformResponse: (r: any) => r.data,
      keepUnusedDataFor: 0
    }),
    getSections: builder.query<Record<string, Section>, number>({
      query: vehicleId => ({
        url: `vehicles/${vehicleId}/sections`,
        method: 'GET'
      }),
      providesTags: ['VehicleSections'],
      keepUnusedDataFor: 0,
      transformResponse: (r: any) => r.data
    }),
    getOfflineFirstSections: builder.query<{ data: Record<string, Section> }, number>({
      providesTags: ['VehicleSections'],
      // @ts-expect-error bugged
      queryFn: async (vehicleId, _utils, _extraOptions, baseQuery) => {
        try {
          if (navigator.onLine) {
            const response = await baseQuery({ url: `/vehicles/${vehicleId}/sections ` });
            if ('data' in response) {
              await db.sectionsCache.put({ id: vehicleId, data: JSON.stringify((response.data as any)?.data) });
            }
            return response as QueryReturnValue<Record<string, Section>, any, any>;
          }

          const local = await db.sectionsCache
            .where('vehicleId')
            .equals(vehicleId)
            .first();

          if (!local) throw new Error('No local data');

          return { data: local.data };
        } catch (error) {
          console.error(error);
          return { error };
        }
      },
      keepUnusedDataFor: 0,
      transformResponse: (r: any) => r.data
    }),
    patchVehicleSection: builder.mutation<
      { notes_id: number | undefined },
      { vehicleId: number; sectionId: number; notes?: string; aiNotes?: string; grade?: string }
    >({
      query: ({ vehicleId, sectionId, ...payload }) => ({
        url: `vehicles/${vehicleId}/sections/${sectionId}`,
        method: 'PATCH',
        body: payload
      }),
      transformResponse: (r: any) => r.data
    }),
    addCheckbox: builder.mutation<{ data: CheckboxField }, AddCheckboxArgs>({
      query: ({ vehicleId, sectionId, payload }) => ({
        url: `vehicles/${vehicleId}/sections/${sectionId}/checkboxes`,
        method: 'POST',
        body: payload
      })
    }),
    updateCheckbox: builder.mutation<{ notes_id?: number }, UpdateCheckboxArgs>({
      query: ({ checkboxId, vehicleId, sectionId, payload }) => ({
        url: `vehicles/${vehicleId}/sections/${sectionId}/checkboxes/${checkboxId}`,
        method: 'PATCH',
        body: payload
      }),
      transformResponse: (r: any) => r.data
    }),
    removeCheckbox: builder.mutation<void, RemoveCheckboxArgs>({
      query: ({ checkboxId, vehicleId, sectionId }) => ({
        url: `vehicles/${vehicleId}/sections/${sectionId}/checkboxes/${checkboxId}`,
        method: 'DELETE'
      })
    }),
    uploadSectionImage: builder.mutation<
      { data: ImageField },
      {
        image: {
          url: string;
          notes: string;
          index?: number;
          type?: string;
          grade?: number;
          is_damaged?: boolean;
        };
        vehicleId: number;
        sectionId: number;
      }
    >({
      query: ({ vehicleId, image, sectionId }) => {
        const formData = new FormData();
        const file = dataURLtoFile(image.url);
        formData.append(`file`, file);
        if (image.notes != null) {
          formData.append('notes', image.notes);
        }
        if (image.index != null) {
          formData.append('index', image.index.toString());
        }
        if (image.grade != null) {
          formData.append('grade', image.grade.toString());
        }
        if (image.type) {
          formData.append('type', image.type);
        }
        if (image.is_damaged === true) {
          formData.append('is_damaged', 'true');
        }

        return {
          url: `vehicles/${vehicleId}/sections/${sectionId}/images`,
          method: 'POST',
          body: formData,
          formData: true
        };
      }
    }),
    uploadBulkSectionImages: builder.mutation<
      { data: ImageField[] },
      { images: File[]; vehicleId: number; sectionId: number }
    >({
      query: ({ vehicleId, images, sectionId }) => {
        const formData = new FormData();
        for (const image of images) {
          formData.append(`images[]`, image);
        }
        return {
          url: `vehicles/${vehicleId}/sections/${sectionId}/images/bulk`,
          method: 'POST',
          body: formData,
          formData: true
        };
      }
    }),
    removeSectionImage: builder.mutation<void, { imageId: number; vehicleId: number; sectionId: number }>({
      query: ({ vehicleId, imageId, sectionId }) => {
        return {
          url: `vehicles/${vehicleId}/sections/${sectionId}/images/${imageId}`,
          method: 'DELETE'
        };
      }
    }),
    updateSectionImageNotes: builder.mutation<
      { notes_id?: number },
      {
        imageId: number;
        vehicleId: number;
        sectionId: number;
        notes?: string;
        grade?: number;
        isDamaged?: boolean;
      }
    >({
      query: ({ vehicleId, imageId, sectionId, notes, grade, isDamaged }) => {
        return {
          url: `vehicles/${vehicleId}/sections/${sectionId}/images/${imageId}`,
          method: 'POST',
          body: {
            notes,
            grade,
            isDamaged
          }
        };
      },
      transformResponse: (r: any) => r.data
    }),
    syncListerForm: builder.mutation<SyncListerFormResponse, any>({
      query: ({ vehicleId, sectionUpdates = [], imageUpdates = [], removedImages = [], vehicleUpdates = [] }) => {
        const formData = new FormData();

        const updatesToFormData = (updates, prefix) => {
          for (const i in updates) {
            const sectionUpdate = updates[i];
            formData.append(`${prefix}[${i}][entityId]`, sectionUpdate.entityId);
            formData.append(`${prefix}[${i}][entityType]`, sectionUpdate.entityType);
            for (const key in sectionUpdate.payload) {
              let value = sectionUpdate.payload[key] ?? '';
              if (key === 'file' && !(value instanceof File)) {
                console.log(value, 'value');
                value = value instanceof ArrayBuffer ? new File([value], generateRandomId()) : dataURLtoFile(value);
              }
              if (key === 'video' && value && typeof value !== 'string') {
                value = new Blob(value, { type: 'video/mp4' });
              }
              formData.append(`${prefix}[${i}][payload][${key}]`, value);
            }
          }
        };

        updatesToFormData(sectionUpdates, 'sectionUpdates');
        updatesToFormData(imageUpdates, 'imageUpdates');

        for (const i in removedImages) {
          const img = removedImages[i];
          formData.append(`removedImages[${i}]`, img);
        }

        for (const i in vehicleUpdates) {
          const value = vehicleUpdates[i];
          formData.append(`vehicleUpdates[${i}][key]`, value.key);
          formData.append(`vehicleUpdates[${i}][value]`, value.value);
        }

        return {
          url: `vehicles/${vehicleId}/sections/sync`,
          method: 'POST',
          body: formData,
          formData: true
        };
      },
      // invalidatesTags: ['VehicleSections'],
      transformResponse: (r: any) => r.data
    }),
    fetchAiSuggestions: builder.mutation<SectionNotes, { vehicleId: number; sectionId: number }>({
      query: ({ vehicleId, sectionId }) => {
        return {
          url: `vehicles/${vehicleId}/sections/${sectionId}/ai-notes/fetch`,
          method: 'POST'
        };
      },
      transformResponse: (r: any) => r.data
    }),
    useAiSuggestions: builder.mutation<SectionNotes, { vehicleId: number; sectionId: number }>({
      query: ({ vehicleId, sectionId }) => {
        return {
          url: `vehicles/${vehicleId}/sections/${sectionId}/ai-notes/use`,
          method: 'POST'
        };
      },
      transformResponse: (r: any) => r.data
    }),
    fetchAiPreview: builder.mutation<string, string>({
      query: notes => ({
        url: `ai/messages`,
        method: 'POST',
        body: { notes }
      }),
      transformResponse: (r: any) => r.data
    }),
    updateAiNotes: builder.mutation<string, { vehicleId: number; notesId: number; message: string }>({
      query: ({ notesId, message, vehicleId }) => {
        return {
          url: `vehicles/${vehicleId}/sections/${notesId}/ai-notes`,
          method: 'PATCH',
          body: { message }
        };
      }
    })
  }),
  overrideExisting: true
});

export const {
  useGetSectionConfigQuery,
  useGetSectionsOrderQuery,
  useUpdateSectionsOrderMutation,
  useGetQuestionsQuery,
  usePatchVehicleSectionMutation,
  useAddCheckboxMutation,
  useRemoveCheckboxMutation,
  useUpdateCheckboxMutation,
  useGetSectionsQuery,
  useGetOfflineFirstSectionsQuery,
  useUploadSectionImageMutation,
  useRemoveSectionImageMutation,
  useUpdateSectionImageNotesMutation,
  useUploadBulkSectionImagesMutation,
  useSyncListerFormMutation,
  useFetchAiSuggestionsMutation,
  useUseAiSuggestionsMutation,
  useFetchAiPreviewMutation,
  useUpdateAiNotesMutation,
  useGetObdImagesQuery,
  useSaveObdImagesMutation
} = vehicleSectionsApiSlice;

export default vehicleSectionsApiSlice;
